import React, { useState, useEffect } from 'react';

// Páginas
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import ComunicacoesPage from './pages/ComunicacoesPage';
import ConfiguracoesPage from './pages/ConfiguracoesPage';
import RelatoriosPage from './pages/RelatoriosPage';
import CalendarioPage from './pages/CalendarioPage';
import AdminDashboardPage from './pages/AdminDashboardPage';

// Layout compartilhado
import AppLayout from './components/AppLayout';

// Serviços
import { AuthService } from './services/authService';
import { SyncService } from './services/syncService';

// Configuração
import { APP_CONFIG } from './config/theme';

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('landing');
  const [shouldOpenSyncModal, setShouldOpenSyncModal] = useState(false);

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);
      
      if (user) {
        setCurrentPage('dashboard');
        
        if (!user.isDemoUserManual) {
          console.log('🚀 Inicializando sincronização automática para usuário:', user.uid);
          SyncService.initializeUserSync(user.uid).then(initialized => {
            if (initialized) {
              console.log('✅ Sincronização automática inicializada com sucesso');
            } else {
              console.log('⚠️ Sincronização automática não configurada (usuário pode precisar fazer onboarding)');
            }
          }).catch(error => {
            console.error('❌ Erro ao inicializar sincronização automática:', error);
          });
        }
      } else {
        setCurrentPage('landing');
        
        if (currentUser?.uid && !currentUser?.isDemoUserManual) {
          console.log('🧹 Limpando timers de sincronização para usuário:', currentUser.uid);
          SyncService.stopUserSync(currentUser.uid);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      if (currentUser?.uid && !currentUser?.isDemoUserManual) {
        console.log('🧹 Limpando timers de sincronização antes do logout');
        SyncService.stopUserSync(currentUser.uid);
      }
      
      await AuthService.signOut();
      setCurrentUser(null);
      setCurrentPage('landing');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Suporta navegação com parâmetros: 'auth:login', 'auth:cadastro'
  const [authMode, setAuthMode] = useState('login');
  
  const handleNavigate = (page) => {
    // Verificar se tem parâmetro (ex: 'auth:cadastro')
    if (page.includes(':')) {
      const [pageName, param] = page.split(':');
      if (pageName === 'auth') {
        setAuthMode(param === 'cadastro' ? 'cadastro' : 'login');
      }
      setCurrentPage(pageName);
    } else {
      setCurrentPage(page);
    }
  };

  const handleUserAuthenticated = (user) => {
    setCurrentUser(user);
    setCurrentPage('dashboard');
  };

  const handleDemoLogin = () => {
    // Criar usuário demo
    const demoUser = AuthService.createDemoUser();
    setCurrentUser(demoUser);
    setCurrentPage('dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-blue-900 font-medium text-lg">Carregando {APP_CONFIG.name}...</p>
        </div>
      </div>
    );
  }

  // Renderizar página baseada no estado atual
  const renderCurrentPage = () => {
    // Páginas públicas (sem sidebar)
    if (currentPage === 'landing') {
      return (
        <LandingPage
          onUserAuthenticated={handleUserAuthenticated}
          onNavigate={handleNavigate}
        />
      );
    }
    
    if (currentPage === 'auth') {
      return (
        <AuthPage
          onUserAuthenticated={handleUserAuthenticated}
          onNavigate={handleNavigate}
          onDemoLogin={handleDemoLogin}
          initialMode={authMode}
        />
      );
    }

    // Páginas internas (com sidebar via AppLayout)
    const renderInternalPage = () => {
      switch (currentPage) {
        case 'dashboard':
          return (
            <DashboardPage
              currentUser={currentUser}
              onSignOut={handleSignOut}
              onNavigate={handleNavigate}
              isInsideLayout={true}
            />
          );
        
        case 'comunicacoes':
          return (
            <ComunicacoesPage
              currentUser={currentUser}
              onNavigate={handleNavigate}
            />
          );
        
        case 'relatorios':
          return (
            <RelatoriosPage
              currentUser={currentUser}
              onNavigate={handleNavigate}
            />
          );
        
        case 'calendario':
          return (
            <CalendarioPage
              currentUser={currentUser}
              onNavigate={handleNavigate}
            />
          );
        
        case 'admin-dashboard':
          return (
            <AdminDashboardPage
              currentUser={currentUser}
              onNavigate={handleNavigate}
            />
          );
        
        case 'settings':
          return (
            <ConfiguracoesPage
              currentUser={currentUser}
              onNavigate={handleNavigate}
            />
          );
        
        default:
          return (
            <DashboardPage
              currentUser={currentUser}
              onSignOut={handleSignOut}
              onNavigate={handleNavigate}
              isInsideLayout={true}
              shouldOpenSyncModal={shouldOpenSyncModal}
              onSyncModalOpened={() => setShouldOpenSyncModal(false)}
            />
          );
      }
    };

    // Função para abrir modal de busca de prazos
    const handleBuscarPrazos = () => {
      if (currentPage !== 'dashboard') {
        setCurrentPage('dashboard');
      }
      setShouldOpenSyncModal(true);
    };

    // Envolver páginas internas com o AppLayout (sidebar)
    return (
      <AppLayout
        currentUser={currentUser}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        onSignOut={handleSignOut}
        onBuscarPrazos={handleBuscarPrazos}
      >
        {renderInternalPage()}
      </AppLayout>
    );
  };

  return (
    <div className="App">
      {renderCurrentPage()}
    </div>
  );
}

export default App;
